import React from 'react';
import { Target } from '@phosphor-icons/react';

interface GoalRow {
    id: string | number;
    title: string;
    description?: string;
    progress: number;
    startDate?: string;
    endDate?: string;
    dueDate?: string;
    categoryName?: string;
    totalActivities: number;
    completedActivities: number;
    programName?: string;
}

interface MyCoachingGoalProgressWidgetProps {
    data?: GoalRow[] | null;
    loading?: boolean;
    error?: string;
}

const getProgressColor = (progress: number) => {
    if (progress >= 100) return { bar: 'bg-emerald-500', text: 'text-emerald-600' };
    if (progress >= 60) return { bar: 'bg-emerald-500', text: 'text-emerald-600' };
    if (progress >= 30) return { bar: 'bg-amber-500', text: 'text-amber-600' };
    return { bar: 'bg-red-500', text: 'text-red-600' };
};

const formatDate = (dateStr?: string) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const MyCoachingGoalProgressWidget: React.FC<MyCoachingGoalProgressWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Target className="w-8 h-8" />
                        <span className="text-sm">No coaching goals</span>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((goal) => {
                            const progress = Number(goal.progress) || 0;
                            const colors = getProgressColor(progress);
                            const totalAct = Number(goal.totalActivities) || 0;
                            const doneAct = Number(goal.completedActivities) || 0;
                            return (
                                <div key={goal.id} className="p-3 border border-gray-100 rounded-lg">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-800 truncate">{goal.title}</div>
                                            {goal.categoryName && (
                                                <span className="text-[10px] text-gray-400">{goal.categoryName}</span>
                                            )}
                                        </div>
                                        <span className={`text-sm font-bold ${colors.text} ml-2 shrink-0`}>{progress}%</span>
                                    </div>
                                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                                        <div className={`h-full ${colors.bar} rounded-full transition-all`} style={{ width: `${progress}%` }} />
                                    </div>
                                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                                        <span>Due: {formatDate(goal.dueDate)}</span>
                                        {totalAct > 0 && <span>Activities: {doneAct}/{totalAct}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
